import React from 'react';

import useSortAndOrder, { OrderDirection } from 'src/hooks/useSortAndOrder';
import { type SortParameters } from 'src/types';

export type SortProps<FieldType> = (field: FieldType) => {
  order: ReturnType<ReturnType<typeof useSortAndOrder>['getOrderArgument']>;
  handleChangeOrder: ReturnType<ReturnType<typeof useSortAndOrder>['getHandleChangeArgument']>;
};

export type SortAndOrderState<FieldType> = {
  sortProps: SortProps<FieldType>;
  sortParameters: SortParameters<FieldType>;
  setSortParameters: React.Dispatch<React.SetStateAction<SortParameters<FieldType>>>;
};

type UseSortAndOrderState = <FieldType extends string = string>(
  initialField: FieldType,
  initialOrder?: OrderDirection,
  resetOnDefaultsChange?: boolean,
) => SortAndOrderState<FieldType>;

/**
 * Type-safe sorting order state by field type.
 * @example
 * const { sortParameters, sortProps } = useSortAndOrderState<keyof Deal>('name');
 * // You can use sortParameters as a part of request (they are { sort: FieldType, order : 'ASC' | 'DESC' })
 * // and then simply:
 * <Table.Th {...sortProps('name')}>{t('NAME')}</Table.Th>
 * @param initialField - A field for default (ascending) sorting.
 * @param initialOrder - A default sorting direction.
 * @param resetOnDefaultsChange - Whether the state need to be set to defaults when defaults change.
 * (this is useful when you want to have different initial sorting based on conditions)
 * @returns Sort and order state.
 */
export const useSortAndOrderState: UseSortAndOrderState = (
  initialField,
  initialOrder = OrderDirection.ASC,
  resetOnDefaultsChange = false,
) => {
  const [sortParameters, setSortParameters] = React.useState<SortParameters<typeof initialField>>({
    sort: initialField,
    order: initialOrder,
  });
  const { getHandleChangeArgument, getOrderArgument } = useSortAndOrder(sortParameters, setSortParameters);

  // We use this like <td {...sortProps(...)}>. So, we need to recreate it every time.
  // eslint-disable-next-line no-restricted-syntax
  const sortProps = React.useCallback<SortProps<typeof initialField>>(
    (field) => ({
      order: getOrderArgument(field),
      handleChangeOrder: getHandleChangeArgument(field),
    }),
    [getHandleChangeArgument, getOrderArgument],
  );

  React.useEffect(() => {
    if (resetOnDefaultsChange) {
      setSortParameters((sortState) => {
        if (sortState.sort !== initialField || sortState.order !== initialOrder) {
          return { sort: initialField, order: initialOrder };
        }
        return sortState;
      });
    }
  }, [resetOnDefaultsChange, initialField, initialOrder]);

  return React.useMemo<SortAndOrderState<typeof initialField>>(
    () => ({ sortProps, sortParameters, setSortParameters }),
    [sortProps, sortParameters, setSortParameters],
  );
};

export { OrderDirection, type SortParameters };
